// Custom engraved plaques and brass plates. Targets: custom plaques 6,600
// (KD 11, added 2026-11-03), plaque engraving 2,900
// (KD 0), brass plate engraving 1,300, trophy plate engraving 880, custom
// plaques, dedication plaques, donor plaques. Feeds the trophy work.
const {
  engraving,
  quoted,
  setup,
  each,
  small,
  medium,
  large,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  ownItemsAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'plaques',
  slug: 'plaques',
  path: '/services/laser-engraving/plaques/',
  noun: 'plaque',
  crumb: 'Plaques & brass plates',
  title: 'Custom Plaques & Engraved Brass Plates | PrintAWorld',
  description:
    'Custom plaque engraving in NYC: recognition and dedication plaques, brass plates, trophy plates and donor walls in brass, steel, aluminum and wood.',
  eyebrow: 'Plaques & plates / engraved in New York',
  heading:
    'Custom plaques, engraved brass plates and trophy plates, made in NYC.',
  answer: `PrintAWorld engraves custom plaques in New York: recognition and dedication plaques, brass name plates, trophy plates and donor walls, in brass, stainless steel, anodized aluminum and wood. Send the text, or a plate to replace. Engraving is a ${setup} setup plus ${each(small, 'plate or plaque')}; plaque blanks and mounting are ${quoted.toLowerCase()}.`,
  cta: 'Get a plaque quote',
  heroImage: 'walnutPlaque',
  ideaImages: ['dedicationPlate', 'trophyPlates', 'donorWall'],
  images: {
    walnutPlaque: {
      file: 'walnut-plaque',
      alt: 'Walnut recognition plaque with a large brushed brass plate engraved with an award title and name',
      caption: 'Brass on walnut, engraved',
    },
    dedicationPlate: {
      file: 'dedication-plate',
      alt: 'Thick brass dedication plaque engraved Dedicated 2026 mounted on a stone wall',
      caption: 'A brass dedication plaque',
    },
    trophyPlates: {
      file: 'trophy-plates',
      alt: 'Small engraved brass and black aluminum trophy plates with names and years beside a trophy base',
      caption: 'Name plates for trophies',
    },
    donorWall: {
      file: 'donor-wall',
      alt: 'Wall of matching brushed metal donor name plates on walnut in a lobby',
      caption: 'A matching donor wall',
    },
  },
  intro: {
    eyebrow: 'Plates that last',
    title: 'Plaque engraving and brass plates, done to match.',
    paragraphs: [
      'The laser engraves the text into the metal, so it doesn’t wear off a plaque that gets touched or cleaned. Brass, steel, black or gold aluminum and wood all work.',
      'Adding a name to a trophy or a donor wall? Send a photo of an existing plate and we match the size, font and finish.',
    ],
  },
  ideas: {
    eyebrow: 'Plaque ideas',
    title: 'Dedication plaques, trophy plates and donor walls.',
  },
  uses: {
    eyebrow: 'What they are for',
    title: 'Recognition, dedication and name plates.',
    items: [
      {
        label: 'Recognition',
        title: 'Award plaques',
        body: 'Employee, volunteer and partner plaques with a name and a citation.',
      },
      {
        label: 'Buildings',
        title: 'Dedication plaques',
        body: 'Building, room and bench dedications in thick brass or steel.',
      },
      {
        label: 'Trophies',
        title: 'Trophy and name plates',
        body: 'This year’s champion added to a perpetual trophy, matched to the old plates.',
      },
      {
        label: 'Nonprofits',
        title: 'Donor walls',
        body: 'A set of matching plates, and new names added each year.',
      },
      {
        label: 'Memorials',
        title: 'Memorial plaques',
        body: 'A name, dates and a few words, for a bench, a tree or a wall.',
      },
    ],
  },
  materials: {
    link: 'Brass, steel or wood',
    eyebrow: 'Materials',
    title: 'Brass plates, metal plaques and wood.',
    items: [
      {
        title: 'Brass',
        body: 'Polished or brushed brass plates, engraved dark on our MOPA laser.',
      },
      {
        title: 'Stainless steel',
        body: 'Brushed steel for plaques outdoors or in busy spaces.',
      },
      {
        title: 'Anodized aluminum',
        body: 'Black, gold or silver aluminum plates that engrave in bright contrast.',
      },
      {
        title: 'Wood',
        body: `Walnut and maple plaques and backboards, engraved up to ${engraving.workAreas.wood}.`,
      },
    ],
  },
  process: {
    title: 'From your text to a mounted plaque.',
    note: `Standard lead time is ${engraving.standardLeadTime}. Metal plates are engraved up to ${engraving.workAreas.metal}.`,
    steps: [
      {
        title: 'Send the text and size',
        body: 'The wording, the size, the material, or a photo of a plate to match.',
      },
      {
        title: 'Approve a proof',
        body: 'We lay out the plaque and send a proof to check every word.',
      },
      {
        title: 'We engrave',
        body: 'Engraved, cleaned and mounted on the backboard if you ordered one.',
      },
      {
        title: 'Ready to hang',
        body: 'Shipped with mounting hardware, or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Plaque engraving pricing, per piece.',
    cta: 'Price my plaques',
    rows: [
      { label: 'Engraving, plates and plaques', value: small },
      { label: 'Engraving, large signs', value: large },
      { label: 'Plaque blanks and backboards', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Plates that match',
    title: 'Add a name every year, in the same style.',
    paragraphs: [
      'We keep your layout, so next year’s trophy plate or donor name matches this year’s.',
      'Bring your trophy or plaque to the studio, or send the plate, and we engrave and return it.',
    ],
    cta: 'Plan a plaque order',
  },
  faqTitle: 'Plaque engraving questions, answered.',
  faqs: [
    {
      question: 'How much does plaque engraving cost?',
      answer: costAnswer(
        medium,
        `Large signs are ${large.toLowerCase()}. Blanks, backboards and mounting are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'Can you engrave a plate for an existing trophy?',
      answer: `Yes. Send a photo and the size of an existing plate and we match it. ${ownItemsAnswer('trophy or plate')}`,
    },
    {
      question: 'Can you engrave brass plates?',
      answer:
        'Yes. We engrave polished and brushed brass on our MOPA metal laser, with a dark, permanent mark.',
    },
    {
      question: 'How big can a plaque be?',
      answer: `Metal plates are engraved up to ${engraving.workAreas.metal}; wood plaques up to ${engraving.workAreas.wood}. Larger plaques can be made from several plates.`,
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('plaque'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer('Tell us the ceremony or unveiling date.'),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your plaque.',
    body: 'Send the text, the size, the material and how many. We’ll reply with a proof and a written quote.',
    checklist: [
      'The exact wording',
      'Size and material, or a photo of a plate to match',
      'How many plaques or plates',
      'Your in-hand date and delivery ZIP',
    ],
  },
  reviews: ['laser-engraving', 'custom-trophies'],
  moreCards: ['recognitionAwards', 'metalAssetTags', 'fantasyFootball'],
  extraRelated: [['Custom trophies', '/services/custom-trophy/']],
  schema: {
    name: 'Custom Engraved Plaques',
    alternateName: [
      'Plaque engraving',
      'Brass plate engraving',
      'Trophy plate engraving',
      'Dedication plaques',
    ],
    serviceType: 'Laser-engraved plaques and plates',
  },
})
